import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ListGroup,
  Button,
  Overlay,
  Popover,
  Spinner,
} from "react-bootstrap";
import { Chat, useChatStore } from "@/entities/chat";
import { Contact } from "@/entities/contact";
import "./chat-list-item.css";
import { useSearchStore } from "@/entities/search/model";
import { ChatListItem } from "./chat-list-item";

interface ChatListProps {
  chats: Chat[];
  userId: string | null;
  currentChatId: string | undefined;
  onSelectChat: (chat: Chat) => void;
  onSelectChats: (chatId: string) => void;
  getCurrentChatName: (contact: Contact | undefined) => string;
  onDelete: (chatId: string) => void;
}

export const ChatList = ({
  chats,
  userId,
  currentChatId,
  onSelectChat,
  onSelectChats,
  getCurrentChatName,
  onDelete,
}: ChatListProps) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  //++const unreadCountRecord = useScrollStore((state) => state.unreadCount);

  const containerRef = useRef<HTMLDivElement>(null);

  const loadingMore = useChatStore((state) => state.loadingMore);
  const searchQuery = useSearchStore((state) => state.searchQuery);
  const loadMore = useChatStore((state) => state.loadMore);

  const handleScroll = useCallback(() => {
    if (!containerRef.current || loadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMore();
    }
  }, [loadingMore, loadMore]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const checkScroll = () => {
      setHasScroll(el.scrollHeight > el.clientHeight);
    };

    checkScroll();

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLElement>,
    chatId: string
  ) => {
    event.stopPropagation();
    setTarget(event.currentTarget);
    setChatToDelete(chatId);
    setShowConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (chatToDelete) onDelete(chatToDelete);
    setShowConfirm(false);
    setChatToDelete(null);
  };

  const selectedChats = useChatStore((state) => state.selectedChats);

  const hasUnread = useMemo(() => selectedChats.some((chatId) => {
    const chat = chats.find(c => c._id === chatId);
    return chat ? chat.isReaded === false || chat.unreadedMessageCount > 0 : false;
  }), [selectedChats]);

  const hasRead = useMemo(() => selectedChats.some((chatId) => {
    const chat = chats.find(c => c._id === chatId);
    return chat ? (chat.isReaded === undefined || chat.isReaded === true) && chat.unreadedMessageCount === 0 : false;
  }), [selectedChats]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          height: "calc(100% - 46px)",
          overflowY: "auto",
          paddingRight: hasScroll ? 6 : 9,
        }}
        className="scrollbar-custom-y"
      >
        <ListGroup as="ul" variant="flush">
          {chats.map((chat) => (
            <ChatListItem
              key={chat._id}
              chat={chat}
              userId={userId}
              selectedChats={selectedChats}
              hasUnread={hasUnread}
              hasRead={hasRead}
              currentChatId={currentChatId}
              onSelectChat={onSelectChat}
              onSelectChats={onSelectChats}
              getCurrentChatName={getCurrentChatName}
            />
          ))}
        </ListGroup>

        {loadingMore && (
          <div className="text-center my-2">
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>

      {chatToDelete && (
        <Overlay
          show={showConfirm}
          target={target}
          placement="left"
          containerPadding={8}
          rootClose
          onHide={() => setShowConfirm(false)}
        >
          <Popover id={`popover-delete-${chatToDelete}`}>
            <Popover.Body className="p-2 small text-secondary">
              <div className="d-flex flex-column">
                <span className="mb-2">
                  Удалить чат {" "}
                  <strong>
                    {
                      chats.find((c) => c._id === chatToDelete)?.contact
                        .firstName
                    }
                  </strong>
                  ?
                </span>
                <div className="d-flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowConfirm(false)}
                  >
                    Отмена
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleDeleteConfirm}
                  >
                    Удалить
                  </Button>
                </div>
              </div>
            </Popover.Body>
          </Popover>
        </Overlay>
      )}
    </>
  );
};
